import axios from 'axios';
import { ActionTree } from 'vuex';
import { EEntryAction } from '../../document-changelog/types/enums/entry-action';
import { DocumentViewHelper } from '../helpers/document-view.helper';
import { IPublishDocumentPayload } from '../types/publish-document-payload';
import { ISetDocumentRevisionStatusPayload } from '../types/set-document-revision-status-payload';
import { IDocumentViewState } from './document-view.types';
import { alertNamespace, EAlertAction } from '@/alert/store/alert.constants';
import { APIErrorMessages } from '@/api/constants/api-errors.constants';
import { DocumentService } from '@/api/documents/services/document.service';
import { FieldService } from '@/api/fields/services/field.service';
import { IImplementResourceDTO } from '@/api/resources/dtos/implement-resource-dto';
import { ResourceService } from '@/api/resources/services/resource.service';
import {
  EDocumentChangelogAction,
  namespace as documentChangelogNamespace,
} from '@/document-changelog/store/document-changelog.constants';
import { IEntry } from '@/document-changelog/types/entry';
import { IPreviewToken } from '@/document-edit/assembler/document/types/preview-token';
import { IUpdateDocument } from '@/document-edit/assembler/document/types/update-document';
import { DocumentManipulatorHelper } from '@/document-manipulator/helpers/document-manipulator.helper';
import {
  EDocumentViewAction,
  EDocumentViewMutation,
  getDefaultState,
} from '@/document-view/store/document-view.constants';
import { IDocumentEventItem } from '@/document-view/types/document-events-item';
import {
  editorContentNamespace,
  EEditorContentAction,
} from '@/editor/editor-block/store/editor-content.constants';
import { translate } from '@/helpers/translation.helper';
import DocumentViewService from '@/services/document-view.service';
import { RootState } from '@/store/store';
import { IAddRecipientDTO } from '@/types/AddRecipientDTO';
import bugsnagClient from '@lib-shared/bugsnag';
import { EDocumentPageType } from '@lib-shared/enums/document-page-type';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentNotificationPayload } from '@lib-shared/types/document-notification-payload';
import { IDocumentPage } from '@lib-shared/types/document-page';
import { IDocumentRevision } from '@lib-shared/types/document-revision';
import { IDocumentNotification } from '@lib-shared/types/DocumentNotification';
import { ITag } from '@lib-shared/types/Tag';
import { TagService } from '@/api/tags/services/tag.service';
import { ETagAction, tagsModuleName } from '@/tags/store/tag.constants';
import { IRecipient } from '@lib-shared/types/Recipient';
import { ERecipientStatus } from '@lib-shared/recipients/types/enums/recipient-status';
import { IRecipientURL } from '@/api/recipients/types/recipient-url';
import { RecipientService } from '@/api/recipients/services/recipient.service';
import { IEditorRecipientInput } from '@lib-shared/types/editor-recipient-input';

export const actions: ActionTree<IDocumentViewState, RootState> = {
  async [EDocumentViewAction.LoadDocument]({ commit, dispatch }, documentId: string) {
    commit(EDocumentViewMutation.SetLoaded, false);
    commit(EDocumentViewMutation.SetLoading, true);
    try {
      const document: IDocument = await DocumentService.loadDocument(documentId);
      if (DocumentViewHelper.hasPDFPages(document)) {
        const pagesWithFields: IDocumentPage[] = await Promise.all(
          document.pages.map(async (page: IDocumentPage) =>
            page.pageType === EDocumentPageType.DocumentContent
              ? { ...page, fields: await FieldService.loadForPage(document.id, page.id) }
              : page
          )
        );
        dispatch(EDocumentViewAction.LoadDocumentSuccess, { ...document, pages: pagesWithFields });
        return;
      }

      dispatch(EDocumentViewAction.LoadDocumentSuccess, document);
    } catch (error) {
      dispatch(EDocumentViewAction.LoadDocumentFailed, error);
    }
  },

  [EDocumentViewAction.LoadDocumentSuccess]({ dispatch, commit }, document: IDocument) {
    dispatch(EDocumentViewAction.SetDocument, document);
    commit(EDocumentViewMutation.SetLoading, false);
    commit(EDocumentViewMutation.SetLoaded, true);
  },

  async [EDocumentViewAction.LoadDocumentFailed]({ commit }, error) {
    if (error?.message === APIErrorMessages.NoSuchDocument) {
      commit(EDocumentViewMutation.SetDocumentNotFound, true);
    }
    commit(EDocumentViewMutation.SetLoading, false);
    commit(EDocumentViewMutation.SetLoaded, false);
  },

  async [EDocumentViewAction.LoadPageThumbnail](
    { commit, state },
    { pageId, documentId }: { pageId: string; documentId: string }
  ) {
    try {
      const { thumbUrl }: Partial<IDocumentPage> = await DocumentService.loadPageThumbUrl(
        pageId,
        documentId
      );
      const pagesWithThumbURL: IDocumentPage[] = state.document.pages.map((page: IDocumentPage) =>
        page.id === pageId ? { ...page, thumbUrl } : page
      );
      commit(EDocumentViewMutation.SetDocument, { ...state.document, pages: pagesWithThumbURL });
    } catch {
      console.error('Could not load thumbnail for ', pageId);
    }
  },

  [EDocumentViewAction.SetDocument]({ commit }, document: IDocument) {
    commit(EDocumentViewMutation.SetDocument, document);
  },

  async [EDocumentViewAction.AddRecipient](_, addRecipientPayload: IAddRecipientDTO) {
    const body = new FormData();
    const sendManually = addRecipientPayload.sendManually
      ? addRecipientPayload.sendManually.toString()
      : '';

    body.append('action', 'addrecipient');
    body.append('document-id', addRecipientPayload.documentId);
    body.append('document-recipient_email', addRecipientPayload.email);
    body.append('document-recipient_first_name', addRecipientPayload.firstName);
    body.append('document-recipient_last_name', addRecipientPayload.lastName);
    body.append('document-recipient_role', addRecipientPayload.role);
    body.append('send-manually', sendManually);

    const {
      data: { status, recipient_id },
    } = await axios.post(`/save/board`, body);
    if (status !== 1) {
      return false;
    }
    return recipient_id;
  },

  async [EDocumentViewAction.LoadRecipients]({ commit, state }, documentId: string) {
    const { recipients }: IDocument = await DocumentService.loadDocument(documentId);
    commit(EDocumentViewMutation.SetDocument, { ...state.document, recipients });
  },

  async [EDocumentViewAction.LoadEditorRecipientInput]({ commit, dispatch }, documentId: string) {
    commit(EDocumentViewMutation.SetLoading, true);
    try {
      const editorRecipientInput: IEditorRecipientInput[] = await DocumentService.loadEditorRecipientInput(
        documentId
      );
      dispatch(EDocumentViewAction.LoadEditorRecipientInputSuccess, editorRecipientInput);
    } catch (err) {
      dispatch(EDocumentViewAction.LoadEditorRecipientInputFailed);
    }
  },

  [EDocumentViewAction.LoadEditorRecipientInputSuccess](
    { commit },
    editorRecipientInput: IEditorRecipientInput[]
  ) {
    commit(EDocumentViewMutation.SetEditorRecipientInput, editorRecipientInput);
    commit(EDocumentViewMutation.SetLoading, false);
  },

  [EDocumentViewAction.LoadEditorRecipientInputFailed]({ commit, dispatch }) {
    commit(EDocumentViewMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-load-editor-recipient-input'),
      {
        root: true,
      }
    );
  },

  [EDocumentViewAction.ResetState]({ commit, state }) {
    commit(EDocumentViewMutation.SetState, {
      ...getDefaultState(),
      dismissedEditPromptBar: state.dismissedEditPromptBar,
    });
  },

  async [EDocumentViewAction.GetPreviewToken]({ dispatch }, documentId: string) {
    try {
      const previewToken = await DocumentService.getPreviewToken(documentId);
      dispatch(EDocumentViewAction.GetPreviewTokenSuccess, previewToken);
    } catch {
      dispatch(EDocumentViewAction.GetPreviewTokenFailed);
    }
  },

  [EDocumentViewAction.GetPreviewTokenSuccess]({ commit }, previewToken: IPreviewToken) {
    commit(EDocumentViewMutation.SetPreviewToken, previewToken);
  },

  [EDocumentViewAction.GetPreviewTokenFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('failed-to-preview-document'),
      {
        root: true,
      }
    );
  },

  [EDocumentViewAction.ToggleCancelScheduleModal]({ commit }, show: boolean) {
    commit(EDocumentViewMutation.SetCancelScheduleModalVisibility, show);
  },

  async [EDocumentViewAction.LoadDocumentEvents]({ dispatch, commit }, documentId: string) {
    try {
      const events: IDocumentEventItem[] = await DocumentViewService.loadDocumentEvents(documentId);
      commit(EDocumentViewMutation.SetDocumentEvents, events);
    } catch (error) {
      dispatch(
        `${alertNamespace}/${EAlertAction.ShowDanger}`,
        translate('could-not-load-document-events'),
        {
          root: true,
        }
      );
    }
  },

  async [EDocumentViewAction.CreateNewTag]({ dispatch }, tagName: string) {
    try {
      const createdTag: ITag = await TagService.create(tagName);
      dispatch(`${tagsModuleName}/${ETagAction.CreateSuccess}`, createdTag, { root: true });
      dispatch(EDocumentViewAction.AddTag, createdTag);
    } catch (error) {
      dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-create-tag'), {
        root: true,
      });
    }
  },
  async [EDocumentViewAction.AddTag]({ dispatch, commit, state }, tag: ITag) {
    const oldTags = state.document.tags;
    try {
      commit(EDocumentViewMutation.SetTags, [...state.document.tags, tag]);
      await TagService.addMultiple([state.document.id], [tag.id]);
      await TagService.legacyUpdateDocument(state.document.id, state.document.tags);
    } catch (error) {
      dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, translate('could-not-add-tag'), {
        root: true,
      });
      dispatch(EDocumentViewAction.TagFailed, {
        tags: oldTags,
        message: translate('could-not-add-tag'),
      });
    }
  },
  async [EDocumentViewAction.RemoveTag]({ dispatch, commit, state }, tag: ITag) {
    const oldTags = state.document.tags;
    try {
      const tags: ITag[] = state.document.tags.filter((oldTag: ITag) => oldTag.id !== tag.id);
      commit(EDocumentViewMutation.SetTags, tags);
      await TagService.removeMultiple([state.document.id], [tag.id]);
      await TagService.legacyUpdateDocument(state.document.id, tags);
    } catch (error) {
      dispatch(EDocumentViewAction.TagFailed, {
        tags: oldTags,
        message: translate('could-not-remove-tag'),
      });
    }
  },
  async [EDocumentViewAction.TagFailed](
    { dispatch, commit },
    { tags, message }: { tags: ITag[]; message: string }
  ) {
    commit(EDocumentViewMutation.SetTags, tags);
    dispatch(`${alertNamespace}/${EAlertAction.ShowDanger}`, message, {
      root: true,
    });
  },
  async [EDocumentViewAction.UpdateDocument](
    { commit, state, dispatch },
    payload: IUpdateDocument
  ) {
    const previousDocument: IDocument = { ...state.document };
    try {
      const updatedDocument: IDocument = { ...state.document, ...payload };
      commit(EDocumentViewMutation.SetDocument, updatedDocument);
      await DocumentService.updateDocument(payload, state.document.id);
    } catch (error) {
      dispatch(EDocumentViewAction.UpdateDocumentFailed, previousDocument);
    }
  },

  [EDocumentViewAction.UpdateDocumentFailed]({ commit, dispatch }, document: IDocument) {
    commit(EDocumentViewMutation.SetDocument, document);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-update-document'),
      {
        root: true,
      }
    );
  },
  async [EDocumentViewAction.PublishDocument](
    { commit, dispatch, state },
    payload: IPublishDocumentPayload
  ) {
    commit(EDocumentViewMutation.SetLoading, true);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowInfo}`,
      translate(`Updating ${state.document.name}. You will be notified when it's done`),
      {
        root: true,
      }
    );
    try {
      await DocumentService.publishDocument(payload);
      await dispatch(EDocumentViewAction.LoadDocument, payload.documentId);
      dispatch(EDocumentViewAction.PublishDocumentSuccess, payload.documentId);
    } catch (err) {
      dispatch(EDocumentViewAction.PublishDocumentFailed, err);
    }
  },

  [EDocumentViewAction.PublishDocumentSuccess]({ commit }) {
    commit(EDocumentViewMutation.SetLoading, false);
    commit(EDocumentViewMutation.SetLoaded, true);
    commit(EDocumentViewMutation.SetEditorRecipientInput, []);
  },

  [EDocumentViewAction.PublishDocumentFailed]({ state, commit, dispatch }) {
    commit(EDocumentViewMutation.SetLoading, false);
    commit(EDocumentViewMutation.SetLoaded, true);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate(`${state.document.name} could not be updated`),
      {
        root: true,
      }
    );
  },

  async [EDocumentViewAction.DiscardChanges]({ commit, dispatch, state }, documentId: string) {
    commit(EDocumentViewMutation.SetLoading, true);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowInfo}`,
      translate(`Discarding changes to ${state.document.name}`),
      {
        root: true,
      }
    );
    try {
      await DocumentService.discardChanges(documentId);
      dispatch(
        `${editorContentNamespace}/${EEditorContentAction.ResetBlocks}`,
        {},
        {
          root: true,
        }
      );
      await dispatch(EDocumentViewAction.LoadDocument, documentId);
      dispatch(EDocumentViewAction.DiscardChangesSuccess, documentId);
    } catch (err) {
      dispatch(EDocumentViewAction.DiscardChangesFailed, err);
    }
  },

  [EDocumentViewAction.DiscardChangesSuccess]({ dispatch, commit, state }) {
    commit(EDocumentViewMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate(`Changes to ${state.document.name} have been discarded`),
      {
        root: true,
      }
    );
  },
  [EDocumentViewAction.DiscardChangesFailed]({ commit, dispatch, state }) {
    commit(EDocumentViewMutation.SetLoading, false);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate(`Changes to ${state.document.name} could not be discarded`),
      {
        root: true,
      }
    );
  },

  [EDocumentViewAction.EditorUpdateFailed]({ commit, dispatch, state }) {
    commit(EDocumentViewMutation.SetUpdateFailed);
    dispatch(EDocumentViewAction.DiscardChanges, state.document.id);
  },

  async [EDocumentViewAction.LoadDocumentRevisions]({ commit, dispatch }, documentId: string) {
    commit(EDocumentViewMutation.SetRevisionsLoading, true);
    try {
      const documentRevisions: IDocumentRevision[] = await DocumentService.loadDocumentRevisions(
        documentId
      );
      dispatch(EDocumentViewAction.LoadDocumentRevisionsSuccess, documentRevisions);
    } catch (err) {
      dispatch(EDocumentViewAction.LoadDocumentRevisionsFailed);
    }
  },

  [EDocumentViewAction.LoadDocumentRevisionsSuccess](
    { commit },
    documentRevisions: IDocumentRevision[]
  ) {
    commit(EDocumentViewMutation.SetRevisionsLoading, false);
    commit(EDocumentViewMutation.SetDocumentRevisions, documentRevisions);
  },

  [EDocumentViewAction.LoadDocumentRevisionsFailed]({ commit }) {
    commit(EDocumentViewMutation.SetRevisionsLoading, false);
  },

  [EDocumentViewAction.SetDocumentRevisionStatus](
    { commit, dispatch, state },
    payload: ISetDocumentRevisionStatusPayload
  ) {
    commit(EDocumentViewMutation.SetDocumentRevisionStatus, payload);
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowSuccess}`,
      translate(
        `${state.document.name} is updated. All recipients have been notified of your changes`
      ),
      {
        root: true,
      }
    );
  },

  async [EDocumentViewAction.DisconnectResource]({ state, dispatch }, resourceId: string) {
    try {
      const payload: IImplementResourceDTO = { documentId: state.document.id, resourceId };
      await ResourceService.disconnect(payload);
    } catch (error) {
      dispatch(EDocumentViewAction.DisconnectResourceFailed, error);
    }
  },

  [EDocumentViewAction.DisconnectResourceFailed](_, error: Error) {
    console.error('Could not disconnect resource', error);
  },

  [EDocumentViewAction.UpdatePage]({ state, commit, dispatch }, page: IDocumentPage) {
    const updatedPage: IDocumentPage = { ...page };
    if (page.resourceId) {
      dispatch(EDocumentViewAction.DisconnectResource, page.resourceId);
      updatedPage.resourceId = null;
    }

    commit(
      EDocumentViewMutation.SetPages,
      DocumentManipulatorHelper.updatePage(updatedPage, state.document.pages)
    );
  },

  [EDocumentViewAction.DismissEditPromptBar]({ commit }) {
    commit(EDocumentViewMutation.SetDismissedEditPromptBar);
  },

  [EDocumentViewAction.ReplacePage]({ state, commit }, page: IDocumentPage) {
    const updatedPages: IDocumentPage[] = DocumentManipulatorHelper.updatePage(
      page,
      state.document.pages
    );
    commit(EDocumentViewMutation.SetPages, updatedPages);
  },

  [EDocumentViewAction.AddPages]({ state, dispatch, commit }, pages: IDocumentPage[]) {
    const mappedPages: IDocumentPage[] = DocumentManipulatorHelper.insertNewPages(
      pages,
      state.document.pages
    );
    const entryPayload: { documentId: string; entry: IEntry } = {
      documentId: state.document.id,
      entry: {
        action: EEntryAction.PagesAdded,
        pagePayloads: pages.map((page: IDocumentPage) => ({ id: page.id, type: page.pageType })),
      },
    };
    dispatch(`${documentChangelogNamespace}/${EDocumentChangelogAction.AddEntry}`, entryPayload, {
      root: true,
    });
    commit(EDocumentViewMutation.SetPages, mappedPages);
  },

  async [EDocumentViewAction.RemovePage]({ state, dispatch, commit }, page: IDocumentPage) {
    const filteredPages: IDocumentPage[] = DocumentManipulatorHelper.filterPage(
      page,
      state.document.pages
    );
    commit(EDocumentViewMutation.SetPages, filteredPages);
    const entryPayload: { documentId: string; entry: IEntry } = {
      documentId: state.document.id,
      entry: {
        action: EEntryAction.PageRemoved,
        pagePayloads: [{ id: page.id, type: page.pageType }],
      },
    };
    dispatch(`${documentChangelogNamespace}/${EDocumentChangelogAction.AddEntry}`, entryPayload, {
      root: true,
    });
  },

  [EDocumentViewAction.UpdatePageOrder]({ commit, dispatch, state }, pages: IDocumentPage[]) {
    commit(EDocumentViewMutation.SetPages, pages);
    const entryPayload: { documentId: string; entry: IEntry } = {
      documentId: state.document.id,
      entry: {
        action: EEntryAction.PagesRearranged,
      },
    };
    dispatch(`${documentChangelogNamespace}/${EDocumentChangelogAction.AddEntry}`, entryPayload, {
      root: true,
    });
  },

  async [EDocumentViewAction.LoadDocumentNotifications]({ dispatch, state }) {
    try {
      const notifications: IDocumentNotification[] = await DocumentService.loadNotifications(
        state.document.id
      );
      dispatch(EDocumentViewAction.LoadDocumentNotificationsSuccess, notifications);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(EDocumentViewAction.LoadDocumentNotificationsFailed);
    }
  },

  [EDocumentViewAction.LoadDocumentNotificationsSuccess](
    { commit },
    notifications: IDocumentNotification[]
  ) {
    commit(EDocumentViewMutation.SetDocumentNotifications, notifications);
  },

  [EDocumentViewAction.LoadDocumentNotificationsFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-fetch-notifications-from-document'),
      {
        root: true,
      }
    );
  },

  async [EDocumentViewAction.DeleteDocumentNotification]({ dispatch }, notificationId: string) {
    try {
      await DocumentService.deleteDocumentNotification(notificationId);
      await dispatch(EDocumentViewAction.LoadDocumentNotifications);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(EDocumentViewAction.DeleteDocumentNotificationFailed);
    }
  },

  [EDocumentViewAction.DeleteDocumentNotificationFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowInfo}`,
      translate('could-not-deleted-the-notification'),
      {
        root: true,
      }
    );
  },

  async [EDocumentViewAction.AddDocumentNotification](
    { state, dispatch },
    payload: IDocumentNotificationPayload
  ) {
    try {
      await DocumentService.addDocumentNotification(payload, state.document.id);
      await dispatch(EDocumentViewAction.LoadDocumentNotifications);
    } catch (error) {
      if (bugsnagClient) {
        bugsnagClient.notify(error);
      }
      dispatch(EDocumentViewAction.AddDocumentNotificationFailed);
    }
  },

  [EDocumentViewAction.AddDocumentNotificationFailed]({ dispatch }) {
    dispatch(
      `${alertNamespace}/${EAlertAction.ShowDanger}`,
      translate('could-not-add-the-notification'),
      {
        root: true,
      }
    );
  },
  async [EDocumentViewAction.DocumentUpdated]({ state }) {
    try {
      await DocumentService.documentUpdated(state.document.id);
    } catch (error) {
      console.error(error);
    }
  },

  async [EDocumentViewAction.LoadRecipientURLs]({ dispatch, commit }, documentId: string) {
    commit(EDocumentViewMutation.SetLoadedRecipientLinks, false);
    commit(EDocumentViewMutation.SetLoadingRecipientLinks, true);
    try {
      const recipientURLs: IRecipientURL[] = await RecipientService.loadRecipientSigningPageURLs(
        documentId
      );
      dispatch(EDocumentViewAction.LoadRecipientURLsSuccess, recipientURLs);
    } catch (e) {
      dispatch(EDocumentViewAction.LoadRecipientURLsFailed);
    }
  },
  [EDocumentViewAction.LoadRecipientURLsSuccess]({ commit }, recipientLinks: IRecipientURL[]) {
    commit(EDocumentViewMutation.SetLoadedRecipientLinks, true);
    commit(EDocumentViewMutation.SetLoadingRecipientLinks, false);
    commit(EDocumentViewMutation.SetRecipientURLs, recipientLinks);
  },
  [EDocumentViewAction.LoadRecipientURLsFailed]({ commit }) {
    commit(EDocumentViewMutation.SetLoadedRecipientLinks, false);
    commit(EDocumentViewMutation.SetLoadingRecipientLinks, false);
  },
  async [EDocumentViewAction.StatusUpdated](
    { state, commit },
    {
      recipientId,
      documentId,
      status,
    }: { recipientId?: string; documentId: string; status?: ERecipientStatus }
  ) {
    try {
      const documentStatus: string = await DocumentService.getDocumentStatus(documentId);
      let newRecipients: IRecipient[] = state.document.recipients;
      if (recipientId && status) {
        newRecipients = [...newRecipients].map((recipient: IRecipient) =>
          recipientId === recipient.id ? { ...recipient, status } : recipient
        );
      }
      commit(EDocumentViewMutation.SetDocument, {
        ...state.document,
        status: documentStatus,
        recipients: newRecipients,
      });
    } catch (e) {
      console.error(e);
    }
  },
};
